import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { useFirebase } from '@/contexts/FirebaseContext';
import { getAllCourses } from '@/utils/firestoreService';
import { collection, getDocs, query, where, addDoc, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { Calendar, Clock, BookOpen, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface StudentBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentData: {
    firstName: string;
    lastName: string;
    email: string;
    instructorId: string;
    area: string;
    phone?: string;
    address?: string;
    postcode?: string;
  };
  onBookingComplete: () => void;
}

interface Course {
  id: string;
  title: string;
  type: string;
  subtype: string;
  price: number;
  pricePerHour?: number;
  totalHours?: number;
  description: string;
}

interface Area {
  id: string;
  name: string;
}

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  area: string;
  status: string;
}

const StudentBookingDialog: React.FC<StudentBookingDialogProps> = ({
  open,
  onOpenChange,
  studentData,
  onBookingComplete
}) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    courseId: '',
    courseName: '',
    carType: '',
    date: '',
    time: '',
    selectedHours: 1,
    isRecurring: false,
    acceptTerms: false,
  });
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Data states
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [timeOverlapError, setTimeOverlapError] = useState('');

  // Course details and pricing
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [coursePrice, setCoursePrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [showRecurringOption, setShowRecurringOption] = useState(false);

  const { toast } = useToast();
  const { currentUser, getFirestore } = useFirebase();

  // Initialize form with student data
  useEffect(() => {
    if (open && studentData) {
      setForm(prev => ({
        ...prev,
        // Pre-fill with student data where applicable
      }));
    }
  }, [open, studentData]);

  // Fetch courses on mount
  useEffect(() => {
    if (open) {
      const fetchCourses = async () => {
        setLoadingCourses(true);
        try {
          const data = await getAllCourses();
          console.log('📚 Courses fetched for student booking:', data);
          setCourses(data);
        } catch (e) {
          console.error('Error fetching courses:', e);
          setCourses([]);
        } finally {
          setLoadingCourses(false);
        }
      };
      fetchCourses();
    }
  }, [open]);

  // Fetch instructors when area is available
  useEffect(() => {
    if (open && studentData.area) {
      const fetchInstructors = async () => {
        try {
          const db = getFirestore();
          const instructorsRef = collection(db, 'users');
          const instructorsQuery = query(
            instructorsRef,
            where('role', '==', 'instructor'),
            where('area', '==', studentData.area),
            where('status', '==', 'active')
          );
          const instructorsSnapshot = await getDocs(instructorsQuery);
          const instructorsData = instructorsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Instructor[];
          setInstructors(instructorsData);
        } catch (error) {
          console.error('Error fetching instructors:', error);
          setInstructors([]);
        }
      };
      fetchInstructors();
    }
  }, [open, studentData.area, getFirestore]);

  // Helper functions
  const isBlockLesson = (course: Course) => {
    return course && (
      course.title?.toLowerCase().includes('block') ||
      course.title?.toLowerCase().includes('package') ||
      (course.totalHours && course.totalHours >= 10)
    );
  };

  const isIndividualCourse = (course: Course) => {
    return course.type === 'individual' || course.subtype === 'individual';
  };

  const calculateCoursePricing = (course: Course, hours: number = 1) => {
    if (!course) return { basePrice: 0, totalPrice: 0 };
    
    let basePrice = course.price || 0;
    let totalPrice = basePrice;
    
    const isIndividual = isIndividualCourse(course);
    if (isIndividual && course.pricePerHour) {
      basePrice = course.pricePerHour;
      totalPrice = basePrice * hours;
    }
    
    return { basePrice, totalPrice };
  };

  const handleCourseSelection = (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    setSelectedCourse(course);
    
    console.log('🔍 Course selection details:', {
      courseId,
      course,
      courseTitle: course?.title,
      courseType: course?.type,
      courseSubtype: course?.subtype,
      courseTotalHours: course?.totalHours,
      coursePricePerHour: course?.pricePerHour,
      courseVehicleType: course?.vehicleType,
      courseLicenseType: course?.licenseType
    });
    
    if (course) {
      const isBlock = isBlockLesson(course);
      const isIndividual = isIndividualCourse(course);
      
      setShowRecurringOption(isBlock);
      
      // Reset hours for individual courses
      if (isIndividual) {
        setForm(f => ({ ...f, selectedHours: 1, isRecurring: false }));
      } else {
        setForm(f => ({ ...f, selectedHours: course.totalHours || 1, isRecurring: false }));
      }
      
      // Calculate pricing
      const hours = isIndividual ? 1 : (course.totalHours || 1);
      const { basePrice, totalPrice } = calculateCoursePricing(course, hours);
      setCoursePrice(basePrice);
      setTotalPrice(totalPrice);
    }
  };

  const handleHourSelection = (hours: number) => {
    setForm(f => ({ ...f, selectedHours: hours }));
    
    if (selectedCourse && isIndividualCourse(selectedCourse)) {
      const { totalPrice } = calculateCoursePricing(selectedCourse, hours);
      setTotalPrice(totalPrice);
    }
  };

  // Time conversion utilities
  const timeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Fetch available times - using the same logic as public booking
  useEffect(() => {
    const fetchAvailableTimes = async () => {
      if (!form.date || !form.courseId || !studentData.area || !studentData.instructorId) {
        setAvailableTimes([]);
        return;
      }

      setCheckingAvailability(true);
      setAvailabilityError('');

      try {
        const db = getFirestore();
        const bookedTimes = new Set<string>();
        const unavailableTimes = new Set<string>();
        const LESSON_BUFFER_MINUTES = 15;

        // 1. Check schedules for existing bookings on the selected date FOR THE SELECTED INSTRUCTOR
        console.log('🔍 Checking availability for instructor:', studentData.instructorId, 'on date:', form.date);
        const schedulesRef = collection(db, 'schedules');
        const schedulesQuery = query(
          schedulesRef,
          where('instructorId', '==', studentData.instructorId),
          where('date', '==', form.date),
          where('status', 'in', ['pending', 'confirmed', 'completed'])
        );
        const schedulesSnapshot = await getDocs(schedulesQuery);
        console.log('📅 Found', schedulesSnapshot.docs.length, 'existing bookings for this instructor on this date');
        
        schedulesSnapshot.forEach(doc => {
          const data = doc.data();
          
          // CRITICAL: Skip cancelled lessons - they should not block new bookings
          if (data.status === 'cancelled') {
            console.log('⏭️ Student booking - Skipping cancelled lesson:', {
              startTime: data.startTime,
              studentName: data.studentName,
              status: data.status,
              cancelledAt: data.cancelledAt
            });
            return; // Skip this cancelled lesson
          }
          
          // Add all times in the booking slot as unavailable PLUS buffer time
          const startTime = data.startTime;
          const endTime = data.endTime || data.time;
          
          console.log('🚫 Student booking - Found active booking (blocking time slots):', {
            startTime,
            endTime,
            studentName: data.studentName,
            status: data.status,
            duration: data.duration
          });
          
          if (startTime && endTime) {
            const startMinutes = timeToMinutes(startTime);
            const endMinutes = timeToMinutes(endTime);
            
            // Use duration from schedule if available, otherwise calculate from start/end times
            const duration = data.duration || Math.ceil((endMinutes - startMinutes) / 60);
            const actualEndMinutes = startMinutes + (duration * 60);
            
            // Block lesson time + 15-minute buffer for instructor break
            for (let minutes = startMinutes; minutes < actualEndMinutes + LESSON_BUFFER_MINUTES; minutes += 15) {
              const timeSlot = minutesToTime(minutes);
              bookedTimes.add(timeSlot);
              console.log('🚫 Blocking time slot:', timeSlot, `(lesson: ${startTime}-${endTime}, duration: ${duration}h)`);
            }
          }
        });

        // 2. Check instructor availability for the selected instructor
        const selectedInstructor = instructors.find(i => i.id === studentData.instructorId);
        if (selectedInstructor) {
          const instructorAvailabilityRef = collection(db, 'instructor_availability');
          const availabilityQuery = query(
            instructorAvailabilityRef,
            where('instructorEmail', '==', selectedInstructor.email)
          );
          const availabilitySnapshot = await getDocs(availabilityQuery);
          
          availabilitySnapshot.forEach(doc => {
            const data = doc.data();
            if (data.unavailableSlots) {
              data.unavailableSlots.forEach((slot: any) => {
                if (slot.date === form.date) {
                  if (slot.isFullDay) {
                    // Mark all day as unavailable
                    for (let h = 8; h <= 20; h++) {
                      for (let m = 0; m < 60; m += 15) {
                        const timeSlot = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                        unavailableTimes.add(timeSlot);
                      }
                    }
                  } else if (slot.startTime && slot.endTime) {
                    // Mark specific time range as unavailable
                    const startMinutes = timeToMinutes(slot.startTime);
                    const endMinutes = timeToMinutes(slot.endTime);
                    
                    for (let minutes = startMinutes; minutes < endMinutes; minutes += 15) {
                      const timeSlot = minutesToTime(minutes);
                      unavailableTimes.add(timeSlot);
                    }
                  }
                }
              });
            }
          });
        }

        // 3. Generate available time slots (8:00 AM to 8:00 PM in 15-minute intervals)
        const times: string[] = [];
        const allTimeSlots: string[] = [];
        
        for (let h = 8; h <= 20; h++) {
          for (let m = 0; m < 60; m += 15) {
            const timeSlot = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
            allTimeSlots.push(timeSlot);
            
            // Skip if time is booked or instructor is unavailable
            if (!bookedTimes.has(timeSlot) && !unavailableTimes.has(timeSlot)) {
              times.push(timeSlot);
            } else {
              const reason = bookedTimes.has(timeSlot) ? 'booked' : 'instructor unavailable';
              console.log(`🚫 Time slot ${timeSlot} is ${reason}`);
            }
          }
        }

        console.log('📊 Student Booking - Availability Summary:', {
          totalTimeSlots: allTimeSlots.length,
          availableSlots: times.length,
          bookedSlots: bookedTimes.size,
          unavailableSlots: unavailableTimes.size,
          availableTimes: times,
          lessonsChecked: schedulesSnapshot.docs.length,
          lessonsExcluded: schedulesSnapshot.docs.filter(doc => doc.data().status === 'cancelled').length,
          activeLessons: schedulesSnapshot.docs.filter(doc => doc.data().status !== 'cancelled').length
        });

        setAvailableTimes(times);
        if (times.length === 0) {
          setAvailabilityError('No available times for this date and instructor.');
        } else {
          setAvailabilityError(''); // Clear any previous errors
        }
      } catch (error) {
        console.error('Error checking availability:', error);
        setAvailableTimes([]);
        setAvailabilityError('Error checking availability.');
      } finally {
        setCheckingAvailability(false);
      }
    };

    fetchAvailableTimes();
  }, [form.date, form.courseId, studentData.area, studentData.instructorId, instructors, getFirestore]);

  // Validation functions
  const validateStep1 = () => {
    const errs: any = {};
    if (!form.courseId) errs.courseId = 'Please select a service/lesson';
    if (!form.carType) errs.carType = 'Please select a car type';
    if (!form.date) errs.date = 'Please select a date';
    if (!form.time) errs.time = 'Please select a time';
    
    // CRITICAL: Prevent progression if there's a time overlap error
    if (timeOverlapError) {
      errs.timeOverlap = timeOverlapError;
      console.log('❌ Student booking - Step 1 validation failed due to time overlap:', timeOverlapError);
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs: any = {};
    if (!form.acceptTerms) errs.acceptTerms = 'You must accept the terms and conditions.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Real-time overlap check when time is selected
  const checkTimeSlotOverlap = async (selectedTime: string) => {
    if (!selectedTime || !form.date || !studentData.instructorId || !selectedCourse) {
      return { hasOverlap: false, reason: '' };
    }

    try {
      const db = getFirestore();
      
      // Get all existing lessons for this instructor on this date
      const schedulesRef = collection(db, 'schedules');
      const allLessonsQuery = query(
        schedulesRef,
        where('instructorId', '==', studentData.instructorId),
        where('date', '==', form.date),
        where('status', 'in', ['pending', 'confirmed', 'completed'])
      );
      
      const allLessonsSnapshot = await getDocs(allLessonsQuery);
      
      // Calculate the selected lesson's time range
      const newStartMinutes = timeToMinutes(selectedTime);
      const newDuration = selectedCourse && isIndividualCourse(selectedCourse) 
        ? (form.selectedHours || 1) 
        : (selectedCourse?.totalHours || 1);
      const newEndMinutes = newStartMinutes + (newDuration * 60);
      
      console.log('🔍 Student booking - Real-time overlap check for time selection:', {
        selectedTime,
        newDuration,
        newStartMinutes,
        newEndMinutes,
        totalLessonsFound: allLessonsSnapshot.docs.length
      });
      
      // Check for overlaps with existing lessons
      for (const doc of allLessonsSnapshot.docs) {
        const existingLesson = doc.data();
        
        // Skip cancelled lessons
        if (existingLesson.status === 'cancelled') {
          continue;
        }
        
        const existingStartMinutes = timeToMinutes(existingLesson.startTime);
        const existingDuration = existingLesson.duration || 1;
        const existingEndMinutes = existingStartMinutes + (existingDuration * 60);
        
        // Check for overlap
        const hasOverlap = newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes;
        
        if (hasOverlap) {
          const overlapStart = Math.max(newStartMinutes, existingStartMinutes);
          const overlapEnd = Math.min(newEndMinutes, existingEndMinutes);
          const overlapDuration = (overlapEnd - overlapStart) / 60;
          
          console.log('❌ Student booking - Real-time overlap detected:', {
            selectedTime,
            existingLesson: existingLesson.startTime,
            overlapDuration: `${overlapDuration} hours`,
            existingStudent: existingLesson.studentName
          });
          
          return {
            hasOverlap: true,
            reason: `This time slot overlaps with an existing lesson (${existingLesson.studentName} from ${existingLesson.startTime} to ${minutesToTime(existingEndMinutes)}). Please select a different time.`
          };
        }
      }
      
      return { hasOverlap: false, reason: '' };
    } catch (error) {
      console.error('Error in student booking real-time overlap check:', error);
      return { hasOverlap: false, reason: '' };
    }
  };

  // Final availability check function with comprehensive overlap detection
  const checkFinalAvailability = async (bookingData: any) => {
    try {
      const db = getFirestore();
      
      // Get all existing lessons for this instructor on this date
      const schedulesRef = collection(db, 'schedules');
      const allLessonsQuery = query(
        schedulesRef,
        where('instructorId', '==', bookingData.instructorId),
        where('date', '==', bookingData.date),
        where('status', 'in', ['pending', 'confirmed', 'completed'])
      );
      
      const allLessonsSnapshot = await getDocs(allLessonsQuery);
      
      // Calculate the new lesson's time range
      const newStartMinutes = timeToMinutes(bookingData.time);
      const newDuration = selectedCourse && isIndividualCourse(selectedCourse) 
        ? (form.selectedHours || 1) 
        : (selectedCourse?.totalHours || 1);
      const newEndMinutes = newStartMinutes + (newDuration * 60);
      
      console.log('🔍 Student Booking - Final availability check:', {
        newLesson: {
          startTime: bookingData.time,
          duration: newDuration,
          startMinutes: newStartMinutes,
          endMinutes: newEndMinutes
        },
        totalLessonsFound: allLessonsSnapshot.docs.length,
        cancelledLessons: allLessonsSnapshot.docs.filter(doc => doc.data().status === 'cancelled').length,
        activeLessons: allLessonsSnapshot.docs.filter(doc => doc.data().status !== 'cancelled').length
      });
      
      // Check for overlaps with existing lessons
      for (const doc of allLessonsSnapshot.docs) {
        const existingLesson = doc.data();
        
        // CRITICAL: Skip cancelled lessons - they should not block new bookings
        if (existingLesson.status === 'cancelled') {
          console.log('⏭️ Student booking - Final check - Skipping cancelled lesson:', {
            startTime: existingLesson.startTime,
            studentName: existingLesson.studentName,
            status: existingLesson.status,
            cancelledAt: existingLesson.cancelledAt
          });
          continue; // Skip this cancelled lesson
        }
        
        const existingStartMinutes = timeToMinutes(existingLesson.startTime);
        const existingDuration = existingLesson.duration || 1;
        const existingEndMinutes = existingStartMinutes + (existingDuration * 60);
        
        console.log('🔍 Student booking - Final check - Checking against active lesson:', {
          existingLesson: {
            startTime: existingLesson.startTime,
            duration: existingDuration,
            startMinutes: existingStartMinutes,
            endMinutes: existingEndMinutes,
            studentName: existingLesson.studentName,
            status: existingLesson.status
          }
        });
        
        // Check for overlap: new lesson overlaps with existing if:
        // 1. New starts before existing ends AND new ends after existing starts
        const hasOverlap = newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes;
        
        if (hasOverlap) {
          const overlapStart = Math.max(newStartMinutes, existingStartMinutes);
          const overlapEnd = Math.min(newEndMinutes, existingEndMinutes);
          const overlapDuration = (overlapEnd - overlapStart) / 60;
          
          console.log('❌ Student booking - Overlap detected:', {
            overlapStart: minutesToTime(overlapStart),
            overlapEnd: minutesToTime(overlapEnd),
            overlapDuration: `${overlapDuration} hours`,
            existingStudent: existingLesson.studentName
          });
          
          return {
            isAvailable: false,
            reason: `This time slot overlaps with an existing lesson (${existingLesson.studentName} from ${existingLesson.startTime} to ${minutesToTime(existingEndMinutes)}). Please select a different time.`
          };
        }
      }
      
      // Check instructor unavailability
      const selectedInstructor = instructors.find(i => i.id === bookingData.instructorId);
      if (selectedInstructor) {
        const instructorAvailabilityRef = collection(db, 'instructor_availability');
        const availabilityQuery = query(
          instructorAvailabilityRef,
          where('instructorEmail', '==', selectedInstructor.email)
        );
        const availabilitySnapshot = await getDocs(availabilityQuery);
        
        for (const doc of availabilitySnapshot.docs) {
          const data = doc.data();
          if (data.unavailableSlots) {
            for (const slot of data.unavailableSlots) {
              if (slot.date === bookingData.date) {
                if (slot.isFullDay) {
                  return {
                    isAvailable: false,
                    reason: 'The instructor is unavailable for the entire day.'
                  };
                } else if (slot.startTime && slot.endTime) {
                  const slotStart = timeToMinutes(slot.startTime);
                  const slotEnd = timeToMinutes(slot.endTime);
                  const bookingStart = timeToMinutes(bookingData.time);
                  
                  if (bookingStart >= slotStart && bookingStart < slotEnd) {
                    return {
                      isAvailable: false,
                      reason: 'The instructor is unavailable during this time slot.'
                    };
                  }
                }
              }
            }
          }
        }
      }
      
      return { isAvailable: true };
    } catch (error) {
      console.error('Error checking final availability:', error);
      return {
        isAvailable: false,
        reason: 'Error checking availability. Please try again.'
      };
    }
  };

  // Submit booking
  const handleSubmit = async () => {
    if (!validateStep2()) return;

    setIsSubmitting(true);
    try {
      // Final availability check before creating booking (prevents race conditions)
      console.log('🔍 Performing final availability check...');
      const finalAvailabilityCheck = await checkFinalAvailability({
        instructorId: studentData.instructorId,
        date: form.date,
        time: form.time
      });
      
      if (!finalAvailabilityCheck.isAvailable) {
        toast({
          title: "Time Slot Unavailable",
          description: finalAvailabilityCheck.reason || 'Time slot is no longer available',
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      console.log('✅ Final availability check passed');

      const db = getFirestore();
      
      // Get complete course data from Firestore
      let courseData = null;
      if (form.courseId) {
        try {
          const courseDoc = await getDoc(doc(db, 'courses', form.courseId));
          if (courseDoc.exists()) {
            courseData = courseDoc.data();
            console.log('📚 Course data fetched from Firestore:', courseData);
          } else {
            console.warn('Course document not found in Firestore, using selectedCourse data');
            courseData = selectedCourse;
          }
        } catch (error) {
          console.error('Error fetching course data from Firestore:', error);
          console.log('Falling back to selectedCourse data:', selectedCourse);
          courseData = selectedCourse;
        }
      }
      
      // Ensure we have course data
      if (!courseData && selectedCourse) {
        courseData = selectedCourse;
        console.log('Using selectedCourse as courseData:', courseData);
      }
      
      // Calculate lesson duration
      let lessonDuration = 1;
      if (selectedCourse && isIndividualCourse(selectedCourse)) {
        lessonDuration = form.selectedHours || 1;
      } else if (selectedCourse) {
        lessonDuration = selectedCourse.totalHours || 1;
      }

      const startTime = form.time;
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = startMinutes + (lessonDuration * 60);
      const endTime = minutesToTime(endMinutes);

      // Calculate total hours for enrollment
      const totalHours = selectedCourse && isIndividualCourse(selectedCourse) 
        ? form.selectedHours || 1 
        : (courseData?.totalHours || selectedCourse?.totalHours || 1);

      // Create enrollment first
      const enrollmentData = {
        userId: currentUser?.uid || '',
        courseId: form.courseId,
        courseName: courseData?.title || selectedCourse?.title || form.courseName,
        courseType: courseData?.type || selectedCourse?.type || 'practical',
        courseSubtype: courseData?.subtype || selectedCourse?.subtype || '',
        instructorId: studentData.instructorId,
        instructorName: instructors.find(i => i.id === studentData.instructorId)?.firstName + ' ' + instructors.find(i => i.id === studentData.instructorId)?.lastName || '',
        enrollmentStatus: 'enrolled',
        paymentStatus: 'pending',
        totalHours: totalHours,
        hoursTaken: 0,
        hoursRemaining: totalHours,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const enrollmentRef = await addDoc(collection(db, 'enrollments'), enrollmentData);

      // Create schedule with complete course information
      const scheduleData = {
        // Course information - using courseData from Firestore as primary source
        courseName: courseData?.title || selectedCourse?.title || form.courseName || 'Unknown Course',
        courseSubtype: courseData?.subtype || selectedCourse?.subtype || '',
        courseType: courseData?.type || selectedCourse?.type || 'practical',
        
        // Date and time information
        date: form.date,
        startTime: startTime,
        endTime: endTime,
        startMinutes: startMinutes,
        endMinutes: endMinutes,
        duration: lessonDuration,
        
        // Student information
        studentId: currentUser?.uid || '',
        studentName: `${studentData.firstName} ${studentData.lastName}`,
        studentEmail: studentData.email,
        studentPhone: studentData.phone || '',
        studentAddress: studentData.address || '',
        studentPostcode: studentData.postcode || '',
        studentGearbox: form.carType,
        
        // Instructor information
        instructorId: studentData.instructorId,
        instructorName: instructors.find(i => i.id === studentData.instructorId)?.firstName + ' ' + instructors.find(i => i.id === studentData.instructorId)?.lastName || '',
        instructorEmail: instructors.find(i => i.id === studentData.instructorId)?.email || '',
        
        // Enrollment information
        enrollmentId: enrollmentRef.id,
        
        // Status and progress
        status: 'pending',
        hoursTaken: 0,
        hoursRemaining: totalHours,
        totalHours: totalHours,
        
        // Additional fields with proper course data
        licenseType: courseData?.licenseType || 'Not specified',
        vehicleType: courseData?.vehicleType || form.carType || 'Not specified',
        venue: '',
        notes: '',
        instructorNotified: false,
        
        // Registration status
        isRegisteredUser: true,
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      console.log('📝 Schedule data to be created:', scheduleData);
      console.log('📊 Course data summary:', {
        courseData,
        selectedCourse,
        formCourseName: form.courseName,
        finalCourseName: scheduleData.courseName,
        finalCourseType: scheduleData.courseType,
        finalCourseSubtype: scheduleData.courseSubtype,
        finalVehicleType: scheduleData.vehicleType,
        finalLicenseType: scheduleData.licenseType
      });
      await addDoc(collection(db, 'schedules'), scheduleData);

      toast({
        title: "Booking Successful!",
        description: "Your lesson has been booked and enrollment created.",
      });

      onBookingComplete();
      onOpenChange(false);
      
      // Reset form
      setStep(1);
      setForm({
        courseId: '',
        courseName: '',
        carType: '',
        date: '',
        time: '',
        selectedHours: 1,
        isRecurring: false,
        acceptTerms: false,
      });
      setSelectedCourse(null);
      setErrors({});

    } catch (error) {
      console.error('Error creating booking:', error);
      toast({
        title: "Booking Failed",
        description: "There was an error creating your booking. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render step 1: Course and time selection
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-drivewise-700 mb-2">
          Book Your First Lesson
        </h3>
        <p className="text-gray-600">
          Hi {studentData.firstName}! Let's get you started with your first lesson.
        </p>
        {instructors.find(i => i.id === studentData.instructorId) && (
          <div className="mt-2 p-2 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Your assigned instructor:</strong> {instructors.find(i => i.id === studentData.instructorId)?.firstName} {instructors.find(i => i.id === studentData.instructorId)?.lastName}
            </p>
          </div>
        )}
      </div>

      {/* Course Selection */}
      <div>
        <Label className="block text-sm font-medium text-gray-700 mb-1">Select Service / Lesson</Label>
        <Select
          value={form.courseId}
          onValueChange={(value) => {
            const course = courses.find(c => c.id === value);
            setForm(f => ({ ...f, courseId: value, courseName: course ? course.title : '' }));
            handleCourseSelection(value);
          }}
          disabled={loadingCourses}
        >
          <SelectTrigger className={errors.courseId ? 'border-red-500' : ''}>
            <SelectValue placeholder={loadingCourses ? 'Loading...' : 'Select a course'} />
          </SelectTrigger>
          <SelectContent>
            {courses.map(course => (
              <SelectItem key={course.id} value={course.id}>
                {course.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.courseId && <div className="text-red-500 text-xs mt-1">{errors.courseId}</div>}
      </div>

      {/* Course Details and Pricing */}
      {selectedCourse && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          <h4 className="font-medium text-gray-900">{selectedCourse.title}</h4>
          <p className="text-sm text-gray-600">{selectedCourse.description}</p>
          
          {/* Hour Selection for Individual Courses */}
          {isIndividualCourse(selectedCourse) && (
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-2">Select Number of Hours</Label>
              <div className="flex gap-2 flex-wrap">
                {[1, 1.5, 2, 3, 4, 5, 6, 7, 8].map(hours => (
                  <button
                    key={hours}
                    type="button"
                    onClick={() => handleHourSelection(hours)}
                    className={`px-4 py-2 rounded-lg border ${
                      form.selectedHours === hours
                        ? 'bg-drivewise-700 text-white border-drivewise-700'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-drivewise-500'
                    }`}
                  >
                    {hours === 1.5 ? '1.5 Hours' : `${hours} Hour${hours > 1 ? 's' : ''}`}
                  </button>
                ))}
              </div>
              {selectedCourse.pricePerHour ? (
                <div className="text-sm text-gray-600 mt-1">
                  Rate: £{selectedCourse.pricePerHour.toFixed(2)}/hour | Total: £{totalPrice.toFixed(2)} for {form.selectedHours === 1.5 ? '1.5 hours' : `${form.selectedHours} hour${form.selectedHours > 1 ? 's' : ''}`}
                </div>
              ) : (
                <div className="text-sm text-red-500 mt-1">
                  ⚠️ Price per hour not set for this course. Please contact admin.
                </div>
              )}
            </div>
          )}

          {/* Block Lesson Pricing */}
          {!isIndividualCourse(selectedCourse) && (
            <div className="text-sm text-gray-600">
              Total Price: £{totalPrice.toFixed(2)}
            </div>
          )}
        </div>
      )}

      {/* Car Type Selection */}
      <div>
        <Label className="block text-sm font-medium text-gray-700 mb-1">Car Type</Label>
        <Select
          value={form.carType}
          onValueChange={(value) => setForm(f => ({ ...f, carType: value }))}
        >
          <SelectTrigger className={errors.carType ? 'border-red-500' : ''}>
            <SelectValue placeholder="Select car type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="automatic">Automatic</SelectItem>
          </SelectContent>
        </Select>
        {errors.carType && <div className="text-red-500 text-xs mt-1">{errors.carType}</div>}
      </div>

      {/* Date Selection */}
      <div>
        <Label className="block text-sm font-medium text-gray-700 mb-1">Select Date</Label>
        <Input
          type="date"
          value={form.date}
          onChange={(e) => {
            setForm(f => ({ ...f, date: e.target.value, time: '' }));
            setTimeOverlapError(''); // Clear overlap error when date changes
          }}
          min={new Date().toISOString().split('T')[0]}
          className={errors.date ? 'border-red-500' : ''}
        />
        {errors.date && <div className="text-red-500 text-xs mt-1">{errors.date}</div>}
      </div>

      {/* Time Selection */}
      <div>
        <Label className="block text-sm font-medium text-gray-700 mb-1">Select Time</Label>
        <Select
          value={form.time}
          onValueChange={async (value) => {
            setForm(f => ({ ...f, time: value }));
            
            // Clear previous overlap error
            setTimeOverlapError('');
            
            // Check for overlaps if a time is selected
            if (value) {
              const overlapCheck = await checkTimeSlotOverlap(value);
              if (overlapCheck.hasOverlap) {
                setTimeOverlapError(overlapCheck.reason);
              }
            }
          }}
          disabled={!form.date || checkingAvailability}
        >
          <SelectTrigger className={errors.time ? 'border-red-500' : ''}>
            <SelectValue placeholder={
              checkingAvailability ? 'Checking availability...' :
              !form.date ? 'Please select a date first' :
              availableTimes.length === 0 ? 'No available times' :
              'Select a time'
            } />
          </SelectTrigger>
          <SelectContent>
            {availableTimes.map(time => (
              <SelectItem key={time} value={time}>
                {time}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.time && <div className="text-red-500 text-xs mt-1">{errors.time}</div>}
        {errors.timeOverlap && <div className="text-red-500 text-xs mt-1">{errors.timeOverlap}</div>}
        {availabilityError && <div className="text-red-500 text-xs mt-1">{availabilityError}</div>}
        {timeOverlapError && <div className="text-red-500 text-xs mt-1">{timeOverlapError}</div>}
        {availableTimes.length > 0 && !checkingAvailability && (
          <div className="text-green-600 text-xs mt-1">✓ {availableTimes.length} time slot{availableTimes.length !== 1 ? 's' : ''} available</div>
        )}
        {availableTimes.length === 0 && !checkingAvailability && form.date && (
          <div className="text-orange-500 text-xs mt-1">⚠️ No available times for this date. Please try another date.</div>
        )}
        {checkingAvailability && (
          <div className="text-blue-600 text-xs mt-1">🔄 Checking availability...</div>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (validateStep1()) setStep(2);
          }}
          disabled={!form.courseId || !form.carType || !form.date || !form.time || !!timeOverlapError}
          className={timeOverlapError ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : ''}
        >
          {timeOverlapError ? 'Fix Time Conflict' : 'Next'}
        </Button>
      </div>
    </div>
  );

  // Render step 2: Confirmation and terms
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-drivewise-700 mb-2">
          Confirm Your Booking
        </h3>
        <p className="text-gray-600">
          Please review your booking details and accept the terms.
        </p>
      </div>

      {/* Booking Summary */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
        <h4 className="font-medium text-gray-900">Booking Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Course:</span>
            <span className="font-medium">{form.courseName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Duration:</span>
            <span className="font-medium">{form.selectedHours === 1.5 ? '1.5 hours' : `${form.selectedHours} hour${form.selectedHours > 1 ? 's' : ''}`}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Date:</span>
            <span className="font-medium">{form.date}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Time:</span>
            <span className="font-medium">{form.time}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Car Type:</span>
            <span className="font-medium capitalize">{form.carType}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Instructor:</span>
            <span className="font-medium">
              {instructors.find(i => i.id === studentData.instructorId)?.firstName} {instructors.find(i => i.id === studentData.instructorId)?.lastName}
            </span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span className="text-gray-600">Total Price:</span>
            <span className="font-bold text-drivewise-700">£{totalPrice.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="space-y-3">
        <div className="flex items-start space-x-2">
          <Checkbox
            id="acceptTerms"
            checked={form.acceptTerms}
            onCheckedChange={(checked) => setForm(f => ({ ...f, acceptTerms: !!checked }))}
            className={errors.acceptTerms ? 'border-red-500' : ''}
          />
          <Label htmlFor="acceptTerms" className="text-sm text-gray-600">
            I accept the terms and conditions and understand that this booking will create an enrollment and schedule a lesson.
          </Label>
        </div>
        {errors.acceptTerms && <div className="text-red-500 text-xs">{errors.acceptTerms}</div>}
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={() => setStep(1)}>
          Back
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={!form.acceptTerms || isSubmitting}
          className="bg-gradient-to-r from-drivewise-600 to-blue-600 hover:from-drivewise-700 hover:to-blue-700"
        >
          {isSubmitting ? 'Creating Booking...' : 'Confirm Booking'}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-drivewise-600" />
            Book Your First Lesson
          </DialogTitle>
        </DialogHeader>
        
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {step === 1 ? renderStep1() : renderStep2()}
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default StudentBookingDialog;
